import { useMemo, useState } from 'react';
import { gql, useMutation, useQuery, type TypedDocumentNode } from '@apollo/client';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    List,
    ListItemButton,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PlanLimitsList } from '@/graphql/generated/types.generated';
import { TeamPermission } from '@/graphql/generated/types.generated';
import { useTeamPermissions } from '@/providers/team-permissions-provider';
import { PATHS_NO_SUBSCRIPTION_REQUIRED } from '@/constants';
import ProfileAvatar from '@/components/dashboards/profiles/ProfileAvatar';

// ─── Types (mirror the backend otypes; generated docs land after Docker codegen) ──
type OverQuotaRow = Pick<PlanLimitsList, 'key' | 'current' | 'limit'>;
type OverQuotaItem = {
    id: string;
    label: string;
    subLabel?: string | null;
    imageUrl?: string | null;
    networkCode?: string | null;
};
type OverQuotaResource = {
    key: string;
    current: number;
    limit: number;
    removable: boolean;
    items: OverQuotaItem[];
};

// ─── Inline documents (source: src/graphql/plan/*.graphql) ────────────────────────
const WorkspaceOverQuotaDocument: TypedDocumentNode<{ workspaceOverQuota: OverQuotaRow[] }> = gql`
    query workspaceOverQuota {
        workspaceOverQuota {
            key
            current
            limit
        }
    }
`;

const OverQuotaItemsDocument: TypedDocumentNode<{ overQuotaItems: OverQuotaResource[] }> = gql`
    query overQuotaItems {
        overQuotaItems {
            key
            current
            limit
            removable
            items {
                id
                label
                subLabel
                imageUrl
                networkCode
            }
        }
    }
`;

const RemoveOverQuotaItemsDocument: TypedDocumentNode<
    { removeOverQuotaItems: OverQuotaRow[] },
    { input: { key: string; ids: string[] }[] }
> = gql`
    mutation removeOverQuotaItems($input: [OverQuotaDeletionInput!]!) {
        removeOverQuotaItems(input: $input) {
            key
            current
            limit
        }
    }
`;

const FEATURE_NAME_KEY: Record<string, string> = {
    SOCIAL_PROFILES: 'billing.over-quota.feature.profiles',
    PROFILES_GROUPS: 'billing.over-quota.feature.groups',
    COMPETITORS_PROFILES: 'billing.over-quota.feature.competitors',
    DASH_REPORTS_LIMIT: 'billing.over-quota.feature.dashboards',
    TEAM_MEMBERS: 'billing.over-quota.feature.members',
};

// Resources that can actually be removed inline. Non-removable ones (team members —
// no fenix→legacy delete yet) are excluded entirely: they never appear in the modal and
// never block the app on their own.
const REMOVABLE_KEYS = new Set([
    'SOCIAL_PROFILES',
    'PROFILES_GROUPS',
    'COMPETITORS_PROFILES',
    'DASH_REPORTS_LIMIT',
]);

const MANAGE_PLAN_PATH = '/settings/workspace/billing';

/**
 * Whole-app over-quota gate with inline remediation. When the workspace exceeds its
 * plan limits (e.g. after a downgrade), it blocks the app and — for members with the
 * manage-plan permission (Billing/Owner) — lets them remove the excess items per
 * resource until back within limits. Others see a read-only "ask an admin" message.
 * State-driven: clears once the workspace is within its plan.
 */
export default function OverQuotaGate() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const navigate = useNavigate();
    const { teamPermissions } = useTeamPermissions();

    const canManagePlan =
        teamPermissions.includes(TeamPermission.Billing) ||
        teamPermissions.includes(TeamPermission.Owner);

    const exempt = PATHS_NO_SUBSCRIPTION_REQUIRED.some((path) => location.pathname.startsWith(path));

    const { data: overData, refetch: refetchOver } = useQuery(WorkspaceOverQuotaDocument, {
        fetchPolicy: 'network-only',
        skip: exempt,
    });
    // Only removable resources gate the app; a members-only overage never blocks.
    const overRemovable = (overData?.workspaceOverQuota ?? []).filter((r) => REMOVABLE_KEYS.has(r.key));
    const isOver = overRemovable.length > 0;

    const {
        data: itemsData,
        loading: itemsLoading,
        refetch: refetchItems,
    } = useQuery(OverQuotaItemsDocument, {
        fetchPolicy: 'network-only',
        skip: exempt || !isOver || !canManagePlan,
    });

    const [removeItems, { loading: removing }] = useMutation(RemoveOverQuotaItemsDocument, {
        onError: (error) => enqueueSnackbar(t(error.message)),
    });

    // key -> set of item ids marked for deletion
    const [selected, setSelected] = useState<Record<string, Set<string>>>({});
    const [tab, setTab] = useState(0);

    // Only removable resources are shown as tabs — team members are excluded entirely.
    const resources = useMemo(
        () => (itemsData?.overQuotaItems ?? []).filter((r) => r.removable),
        [itemsData],
    );

    const toggle = (key: string, id: string) =>
        setSelected((prev) => {
            const next = new Set(prev[key] ?? []);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return { ...prev, [key]: next };
        });

    // Every (removable) resource must be back within its limit before Confirm is allowed.
    const allWithinLimit = useMemo(
        () => resources.every((r) => r.current - (selected[r.key]?.size ?? 0) <= r.limit),
        [resources, selected],
    );
    const anySelected = Object.values(selected).some((s) => s.size > 0);

    const handleConfirm = async () => {
        const input = Object.entries(selected)
            .filter(([, ids]) => ids.size > 0)
            .map(([key, ids]) => ({ key, ids: [...ids] }));
        if (input.length === 0) return;

        const res = await removeItems({ variables: { input } }).catch(() => null);
        setSelected({});
        if (!res?.data) return; // a failure was already surfaced via the error snackbar

        // Refresh the tabs and the gate driver query so counts stay accurate / the gate closes.
        await Promise.all([refetchItems(), refetchOver()]);

        // No removable resource left over → back within plan. Confirm with a snackbar (the
        // platform's success idiom) and let the gate close itself.
        const remaining = res.data.removeOverQuotaItems.filter((r) => REMOVABLE_KEYS.has(r.key));
        if (remaining.length === 0) {
            enqueueSnackbar(t('billing.over-quota.resolved-body'));
        }
    };

    if (exempt || !isOver) return null;

    // Members without the manage-plan permission can't remediate — point them to an admin.
    if (!canManagePlan) {
        return (
            <Dialog open fullWidth maxWidth="sm" disableEscapeKeyDown>
                <DialogTitle>{t('billing.over-quota.title')}</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary" mb={2}>
                        {t('billing.over-quota.description')}
                    </Typography>
                    <Stack spacing={1}>
                        {overRemovable.map(({ key, current, limit }) => (
                            <Box key={key} display="flex" justifyContent="space-between" gap={2} color="error.dark">
                                <Typography>{t(FEATURE_NAME_KEY[key] ?? '')}</Typography>
                                <Typography>{t('billing.over-quota.usage', { current, limit })}</Typography>
                            </Box>
                        ))}
                    </Stack>
                    <Typography color="text.secondary" mt={2}>
                        {t('billing.over-quota.ask-admin')}
                    </Typography>
                </DialogContent>
            </Dialog>
        );
    }

    // Items still loading — show a skeleton so the modal isn't an empty shell.
    if (itemsLoading && resources.length === 0) {
        return (
            <Dialog open fullWidth maxWidth="sm" disableEscapeKeyDown>
                <DialogTitle>{t('billing.over-quota.title')}</DialogTitle>
                <DialogContent dividers>
                    <Typography color="text.secondary" mb={2}>
                        {t('billing.over-quota.description')}
                    </Typography>
                    <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
                    <Stack spacing={1.5}>
                        {[0, 1, 2].map((i) => (
                            <Stack key={i} direction="row" alignItems="center" gap={1.5}>
                                <Skeleton variant="circular" width={32} height={32} />
                                <Skeleton variant="text" sx={{ flexGrow: 1 }} />
                            </Stack>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between' }}>
                    <Skeleton variant="rounded" width={100} height={36} />
                    <Skeleton variant="rounded" width={150} height={36} />
                </DialogActions>
            </Dialog>
        );
    }

    // Safety net: nothing left to remove (e.g. a refetch cleared the list) — never render an
    // empty tabbed shell; let the guard/refetch close the gate.
    if (resources.length === 0) return null;

    const active = resources[Math.min(tab, resources.length - 1)];

    return (
        <Dialog open fullWidth maxWidth="sm" disableEscapeKeyDown>
            <DialogTitle>{t('billing.over-quota.title')}</DialogTitle>
            <DialogContent dividers>
                <Typography color="text.secondary" mb={2}>
                    {t('billing.over-quota.description')}
                </Typography>

                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
                    {resources.map((r) => (
                        <Tab key={r.key} label={t(FEATURE_NAME_KEY[r.key] ?? '')} />
                    ))}
                </Tabs>
                <Divider sx={{ mb: 1 }} />

                {active &&
                    (() => {
                        const remaining = active.current - (selected[active.key]?.size ?? 0);
                        const stillOver = remaining > active.limit;
                        return (
                            <Box>
                                <Box display="flex" justifyContent="flex-end" mb={1}>
                                    <Typography
                                        variant="body2"
                                        color={stillOver ? 'error.dark' : 'success.main'}
                                    >
                                        {t('billing.over-quota.usage', {
                                            current: remaining,
                                            limit: active.limit,
                                        })}
                                    </Typography>
                                </Box>

                                <List sx={{ maxHeight: 280, overflowY: 'auto' }} dense>
                                    {active.items.map((item) => {
                                            const checked = selected[active.key]?.has(item.id) ?? false;
                                            return (
                                                <ListItemButton
                                                    key={item.id}
                                                    onClick={() => toggle(active.key, item.id)}
                                                    sx={{ gap: 1.5 }}
                                                >
                                                    <Checkbox edge="start" checked={checked} tabIndex={-1} />
                                                    <ProfileAvatar
                                                        name={item.label}
                                                        picture={item.imageUrl ?? undefined}
                                                        sx={{ width: 32, height: 32 }}
                                                    />
                                                    <Box flexGrow={1} minWidth={0}>
                                                        <Stack direction="row" alignItems="center" gap={0.75}>
                                                            {item.networkCode && (
                                                                <Icon
                                                                    component="img"
                                                                    src={`/social/${item.networkCode}.svg`}
                                                                    alt=""
                                                                    sx={{ height: 14, width: 14 }}
                                                                />
                                                            )}
                                                            <Typography noWrap sx={{ textDecoration: checked ? 'line-through' : 'none' }}>
                                                                {item.label}
                                                            </Typography>
                                                        </Stack>
                                                        {item.subLabel && (
                                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                                {item.subLabel}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                            </Box>
                        );
                    })()}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button color="secondary" onClick={() => navigate(MANAGE_PLAN_PATH)}>
                    {t('billing.over-quota.upgrade')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    loading={removing}
                    disabled={!anySelected || !allWithinLimit}
                >
                    {t('billing.over-quota.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
